/**
 * HOF-219 — the one gate every DOCUMENT-level unlink trigger goes through.
 *
 * Same shape as ``edgeUnlinkHandlers`` one layer up, and for the same reason:
 * the triggers (the on-edge control and the right-click menu) converge here
 * BEFORE any network call, so a twin write on a paying tenant's
 * ``gutschrift_match_group`` cannot reach the wire without the operator having
 * seen which two DOCUMENTS it will separate and what joined them.
 *
 * The stakes are a step higher than the line-level gesture. Severing a document
 * edge can split one component into two facts, so it moves money between tiles;
 * and because the exclusion is axis-agnostic it can overrule a wibill ``native``
 * join, which is an exact shared-line-ref match from the source system. The
 * confirmation naming the link kind IS that mitigation.
 *
 * Pure + exported so all of it is testable without mounting ReactFlow.
 */
import { normaliseDocumentEdgeForUnlink, type DocumentEdgeLike } from './documentLinkValidation';
import { buildDocumentUnlinkConfirmationMessage } from './documentUnlinkConfirmation';
import { mapLineLinkErrorToToastMessage } from './lineLinkErrorToast';
import type { LineUnlinkTelemetryPayload, LineUnlinkTrigger } from './lineUnlinkTelemetry';

/** One armed document unlink, held while the confirmation is on screen. */
export interface PendingDocumentUnlink {
  /** The React Flow edge id — so a re-render can tell whether it is still on the canvas. */
  edgeId: string;
  /** Exactly the payload the endpoint takes. */
  args: { settlement_event_id: string; delivery_event_id: string };
  groupId: string | null;
  trigger: LineUnlinkTrigger;
  /** Pre-built so the modal renders no logic. */
  message: string;
}

export interface DocumentUnlinkDeps {
  /** Absent in Storybook / standalone renders — the whole affordance is then inert (AC 14). */
  onDocumentUnlink?: (args: {
    settlement_event_id: string;
    delivery_event_id: string;
  }) => Promise<unknown>;
  showToast: (message: string, tone: 'error') => void;
  openConfirm: (pending: PendingDocumentUnlink) => void;
  reportTelemetry?: (payload: LineUnlinkTelemetryPayload) => void;
  /**
   * Pairs whose write is in the air. Owned by the caller (a ``useRef``) so it
   * survives the memo rebuild every ``groups`` refetch causes — a guard that
   * resets on refetch is not a guard.
   *
   * Confirming closes the modal but deliberately leaves the edge on the canvas
   * until the refetch, so every trigger stays live over a still-running POST.
   * Without this a second confirmation reaches the backend as a historical
   * no-op — which the endpoint accepts with 200 — and would emit a second
   * ``document_unlink`` event claiming a write that never happened, inflating
   * the exact rate the event exists to measure. Same defect HOF-216's fix round
   * found one layer down.
   */
  inFlight: Set<string>;
}

export interface DocumentUnlinkHandlers {
  /** Arm the confirmation for one document edge. Returns whether it opened. */
  requestDocumentUnlink: (edge: DocumentEdgeLike, trigger: LineUnlinkTrigger) => boolean;
  /** The operator said yes. Issues exactly one write. */
  confirmDocumentUnlink: (pending: PendingDocumentUnlink) => Promise<void>;
}

/** Stable key for one document pair. Never rendered — an internal guard key only. */
function pairKey(args: { settlement_event_id: string; delivery_event_id: string }): string {
  return `${args.settlement_event_id}|${args.delivery_event_id}`;
}

export function buildDocumentUnlinkHandlers(deps: DocumentUnlinkDeps): DocumentUnlinkHandlers {
  // Telemetry must never block, delay or fail the operator's action.
  // ``postTelemetryBeacon`` already swallows transport errors, but the caller
  // supplies this function, so the contract is enforced here rather than
  // assumed of every future caller.
  const report = (payload: LineUnlinkTelemetryPayload): void => {
    try {
      deps.reportTelemetry?.(payload);
    } catch {
      /* silent — see above */
    }
  };

  const requestDocumentUnlink = (edge: DocumentEdgeLike, trigger: LineUnlinkTrigger): boolean => {
    if (!deps.onDocumentUnlink) return false;
    const target = normaliseDocumentEdgeForUnlink(edge);
    if (!target) return false;
    if (deps.inFlight.has(pairKey(target))) return false;
    deps.openConfirm({
      edgeId: edge.id ?? `${target.settlement_event_id}_${target.delivery_event_id}`,
      args: {
        settlement_event_id: target.settlement_event_id,
        delivery_event_id: target.delivery_event_id,
      },
      groupId: target.groupId || null,
      trigger,
      message: buildDocumentUnlinkConfirmationMessage({
        settlementLabel: target.settlementLabel,
        deliveryLabel: target.deliveryLabel,
        linkKind: target.linkKind,
      }),
    });
    return true;
  };

  const confirmDocumentUnlink = async (pending: PendingDocumentUnlink): Promise<void> => {
    const call = deps.onDocumentUnlink;
    if (!call) return;
    const key = pairKey(pending.args);
    // Idempotent per pair for as long as the write is in the air. Claimed
    // BEFORE the await so two synchronous confirms cannot both pass the check.
    if (deps.inFlight.has(key)) return;
    deps.inFlight.add(key);
    let landed = false;
    let rejected = false;
    try {
      const result = await call(pending.args);
      // Only a response that says it CHANGED something counts. The endpoint
      // accepts a repeat unlink as a historical no-op and 200s with
      // ``applied: false``, and the canvas keeps the edge on screen until its
      // refetch — so that window is reachable and would otherwise emit a
      // second event for a write that never happened. ``!== false`` is
      // deliberate: an ABSENT field is a backend predating this route's
      // response shape, not evidence of a no-op, and reading it as false would
      // blank the metric across a rolling deploy.
      landed = (result as { applied?: unknown } | null | undefined)?.applied !== false;
    } catch (err) {
      rejected = true;
      deps.showToast(mapLineLinkErrorToToastMessage(err), 'error');
    } finally {
      // Released on EVERY outcome, including a 409 — a guard that a rejected
      // write leaves set would wedge that pair for the rest of the session,
      // which is worse than the duplicate it prevents.
      deps.inFlight.delete(key);
    }
    // AC 15 — BOTH a severing attempt and a rejected one become measurable.
    // A rejection is the more interesting of the two: ``stale_unlink`` at any
    // volume means the canvas and the twin disagree often enough that
    // operators are acting on stale views.
    if (rejected) {
      report({ kind: 'document_unlink_rejected', trigger: pending.trigger, group_id: pending.groupId });
    } else if (landed) {
      report({ kind: 'document_unlink', trigger: pending.trigger, group_id: pending.groupId });
    }
  };

  return { requestDocumentUnlink, confirmDocumentUnlink };
}
